import { supabase } from '../lib/supabase';
import { sendRegionalProfileCreatedEmail } from './emailService';

const BUCKET = 'region-associations';

const resolveStorageUrl = (url, path) => {
  if (url && typeof url === 'string' && url.trim()) {
    return url.trim();
  }
  if (path && typeof path === 'string' && path.trim()) {
    const {
      data: { publicUrl },
    } = supabase.storage.from(BUCKET).getPublicUrl(path.trim());
    return publicUrl || null;
  }
  return null;
};

const normalizeAssociation = (row) => {
  if (!row) return null;
  return {
    id: row.id,
    associationName: row.association_name,
    region: row.region,
    postalAddress: row.postal_address,
    physicalAddress: row.physical_address,
    email: row.email,
    mobile: row.mobile,
    registeredDate: row.registered_date,
    nextElectionDate: row.next_election_date,
    registrationNumber: row.registration_number,
    constitutionUrl: row.constitution_url,
    registrationCertificateUrl: row.registration_certificate_url,
    logoUrl: resolveStorageUrl(row.logo_url, row.logo_path),
    logoPath: row.logo_path || null,
    chairpersonName: row.chairperson_name,
    chairpersonEmail: row.chairperson_email,
    chairpersonMobile: row.chairperson_mobile,
    chairpersonPhotoUrl: resolveStorageUrl(row.chairperson_photo_url, row.chairperson_photo_path),
    secretaryName: row.secretary_name,
    secretaryEmail: row.secretary_email,
    secretaryMobile: row.secretary_mobile,
    secretaryPhotoUrl: resolveStorageUrl(row.secretary_photo_url, row.secretary_photo_path),
    registrationStatus: row.registration_status,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
};

export const regionAssociationService = {
  async uploadFile(file, associationId, folder = 'documents') {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    if (!associationId) throw new Error('Association ID is required for file upload');
    if (!file) throw new Error('File is required');

    const isImage = folder === 'logo' || folder === 'photos';
    if (isImage) {
      const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowed.includes(file.type)) {
        throw new Error('File must be an image (JPEG, PNG, GIF, or WebP)');
      }
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Image file size must be less than 5MB');
      }
    } else {
      const allowed = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
      if (!allowed.includes(file.type)) {
        throw new Error('Document must be PDF, image, or Word document');
      }
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('Document file size must be less than 10MB');
      }
    }

    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 11);
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const ext = sanitizedName.split('.').pop();
    const nameWithoutExt = sanitizedName.substring(0, sanitizedName.lastIndexOf('.')) || sanitizedName;
    const filename = `${timestamp}-${randomStr}-${nameWithoutExt}.${ext}`;
    const filePath = `regions/${associationId}/${folder}/${filename}`;

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(filePath, file, { cacheControl: '3600', upsert: false });

    if (uploadError) {
      if (uploadError.message?.includes('Bucket not found')) {
        throw new Error(
          `Storage bucket "${BUCKET}" not found. Create it in Supabase Storage, then try again.`
        );
      }
      throw new Error(uploadError.message || 'Failed to upload file');
    }

    const {
      data: { publicUrl },
    } = supabase.storage.from(BUCKET).getPublicUrl(filePath);

    return {
      path: filePath,
      url: publicUrl,
      fileName: file.name,
      fileSize: file.size,
    };
  },

  async create(formData) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const payload = {
      association_name: formData.associationName?.trim(),
      region: formData.region,
      postal_address: formData.postalAddress?.trim() || null,
      physical_address: formData.physicalAddress?.trim() || null,
      email: formData.email?.trim() || null,
      mobile: formData.mobile?.trim() || null,
      registered_date: formData.registeredDate || null,
      next_election_date: formData.nextElectionDate || null,
      registration_number: formData.registrationNumber?.trim() || null,
      chairperson_name: formData.chairpersonName?.trim() || null,
      chairperson_email: formData.chairpersonEmail?.trim() || null,
      chairperson_mobile: formData.chairpersonMobile?.trim() || null,
      secretary_name: formData.secretaryName?.trim() || null,
      secretary_email: formData.secretaryEmail?.trim() || null,
      secretary_mobile: formData.secretaryMobile?.trim() || null,
      registration_status: 'pending',
      created_by: user.id,
    };

    const { data, error } = await supabase
      .from('region_associations')
      .insert(payload)
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        throw new Error('A region association already exists for this region.');
      }
      throw new Error(error.message || 'Failed to create region association');
    }

    const associationId = data.id;
    const updates = {};
    const uploadWarnings = [];

    const fileUploads = [
      { file: formData.constitutionFile, folder: 'documents', urlKey: 'constitution_url', pathKey: 'constitution_path', label: 'constitution' },
      {
        file: formData.registrationCertificateFile,
        folder: 'documents',
        urlKey: 'registration_certificate_url',
        pathKey: 'registration_certificate_path',
        label: 'registration certificate',
      },
      { file: formData.logoFile, folder: 'logo', urlKey: 'logo_url', pathKey: 'logo_path', label: 'logo' },
      {
        file: formData.chairpersonPhotoFile,
        folder: 'photos',
        urlKey: 'chairperson_photo_url',
        pathKey: 'chairperson_photo_path',
        label: 'chairperson photo',
      },
      {
        file: formData.secretaryPhotoFile,
        folder: 'photos',
        urlKey: 'secretary_photo_url',
        pathKey: 'secretary_photo_path',
        label: 'secretary photo',
      },
    ];

    for (const item of fileUploads) {
      if (!item.file) continue;
      try {
        const uploaded = await this.uploadFile(item.file, associationId, item.folder);
        updates[item.urlKey] = uploaded.url;
        updates[item.pathKey] = uploaded.path;
      } catch (uploadError) {
        console.error(`Failed to upload ${item.folder}:`, uploadError);
        uploadWarnings.push(`${item.label}: ${uploadError.message || 'upload failed'}`);
      }
    }

    let association = normalizeAssociation(data);

    if (Object.keys(updates).length > 0) {
      const { data: updated, error: updateError } = await supabase
        .from('region_associations')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', associationId)
        .select()
        .single();

      if (updateError) {
        console.error('Failed to save file URLs:', updateError);
        uploadWarnings.push(`file metadata: ${updateError.message}`);
      } else {
        association = normalizeAssociation(updated);
      }
    }

    if (uploadWarnings.length > 0) {
      association = { ...association, uploadWarning: uploadWarnings.join('; ') };
    }

    const notifyEmail =
      association.email || association.chairpersonEmail || association.secretaryEmail;

    if (notifyEmail) {
      try {
        await sendRegionalProfileCreatedEmail({
          to: notifyEmail,
          associationName: association.associationName,
          region: association.region,
          email: association.email,
          mobile: association.mobile,
          registrationNumber: association.registrationNumber,
          chairpersonName: association.chairpersonName,
          secretaryName: association.secretaryName,
          portalUrl: `${window.location.origin}/regions/${association.id}`,
        });
      } catch (emailError) {
        console.error('Failed to send regional profile email:', emailError);
      }
    }

    return association;
  },

  async getAll() {
    const { data, error } = await supabase
      .from('region_associations')
      .select('*')
      .order('association_name', { ascending: true });

    if (error) throw new Error(error.message || 'Failed to load region associations');
    return (data || []).map(normalizeAssociation);
  },

  async getById(id) {
    const { data, error } = await supabase
      .from('region_associations')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw new Error(error.message || 'Failed to load region association');
    return normalizeAssociation(data);
  },

  async update(id, formData) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    if (!id) throw new Error('Association ID is required');

    const payload = {
      association_name: formData.associationName?.trim(),
      region: formData.region,
      postal_address: formData.postalAddress?.trim() || null,
      physical_address: formData.physicalAddress?.trim() || null,
      email: formData.email?.trim() || null,
      mobile: formData.mobile?.trim() || null,
      registered_date: formData.registeredDate || null,
      next_election_date: formData.nextElectionDate || null,
      registration_number: formData.registrationNumber?.trim() || null,
      chairperson_name: formData.chairpersonName?.trim() || null,
      chairperson_email: formData.chairpersonEmail?.trim() || null,
      chairperson_mobile: formData.chairpersonMobile?.trim() || null,
      secretary_name: formData.secretaryName?.trim() || null,
      secretary_email: formData.secretaryEmail?.trim() || null,
      secretary_mobile: formData.secretaryMobile?.trim() || null,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('region_associations')
      .update(payload)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        throw new Error('A region association already exists for this region.');
      }
      throw new Error(error.message || 'Failed to update region association');
    }

    const updates = {};
    const uploadWarnings = [];

    const fileUploads = [
      { file: formData.constitutionFile, folder: 'documents', urlKey: 'constitution_url', pathKey: 'constitution_path', label: 'constitution' },
      {
        file: formData.registrationCertificateFile,
        folder: 'documents',
        urlKey: 'registration_certificate_url',
        pathKey: 'registration_certificate_path',
        label: 'registration certificate',
      },
      { file: formData.logoFile, folder: 'logo', urlKey: 'logo_url', pathKey: 'logo_path', label: 'logo' },
      {
        file: formData.chairpersonPhotoFile,
        folder: 'photos',
        urlKey: 'chairperson_photo_url',
        pathKey: 'chairperson_photo_path',
        label: 'chairperson photo',
      },
      {
        file: formData.secretaryPhotoFile,
        folder: 'photos',
        urlKey: 'secretary_photo_url',
        pathKey: 'secretary_photo_path',
        label: 'secretary photo',
      },
    ];

    for (const item of fileUploads) {
      if (!item.file) continue;
      try {
        const uploaded = await this.uploadFile(item.file, id, item.folder);
        updates[item.urlKey] = uploaded.url;
        updates[item.pathKey] = uploaded.path;
      } catch (uploadError) {
        console.error(`Failed to upload ${item.folder}:`, uploadError);
        uploadWarnings.push(`${item.label}: ${uploadError.message || 'upload failed'}`);
      }
    }

    let association = normalizeAssociation(data);

    if (Object.keys(updates).length > 0) {
      const { data: updated, error: updateError } = await supabase
        .from('region_associations')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (updateError) {
        console.error('Failed to save file URLs:', updateError);
        uploadWarnings.push(`file metadata: ${updateError.message}`);
      } else {
        association = normalizeAssociation(updated);
      }
    }

    if (uploadWarnings.length > 0) {
      association = { ...association, uploadWarning: uploadWarnings.join('; ') };
    }

    return association;
  },
};
